import sha1 from 'sha1';
import { createSelector } from 'reselect';
import { array, eq, nonEmptyArray, record } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { composeProps, createPropsExtender } from '@mews/framework';
import { ServiceOrderNote } from '@mews-commander/types';
import {
    getNotesByServiceOrderIds,
    orderServiceOrderNotes,
} from '@mews-commander/legacy-entities/src/ducks/serviceOrderNotes';
import { getCheckedReservationIds, getCheckedReservationsCount } from '../../../selectors';
import { CompactedNote, NoteWithRankedHash } from './types';

const getNotesGroupedByServiceOrderIds = createSelector(
    getNotesByServiceOrderIds,
    nonEmptyArray.group(
        eq.fromEquals<ServiceOrderNote>((first, second) => first.serviceOrderId === second.serviceOrderId)
    )
);

const getCheckedReservationsNotesGrouped = composeProps(
    getNotesGroupedByServiceOrderIds,
    createPropsExtender('serviceOrderIds', getCheckedReservationIds)
);

const getRankedNotes = createSelector(
    getCheckedReservationsNotesGrouped,
    array.flatMap((serviceOrderNotes) => {
        const seenNotesCount: Record<string, number> = {};
        return serviceOrderNotes.map((note): NoteWithRankedHash => {
            const hash = sha1(note.text);

            seenNotesCount[hash] ??= 0;
            const rank = seenNotesCount[hash];
            seenNotesCount[hash]++;
            return { ...note, rankedHash: `${hash}-${rank}` };
        });
    })
);

export const getCheckedReservationsServiceOrderNotes = createSelector(
    getRankedNotes,
    getCheckedReservationsCount,
    (rankedNotes, checkedReservationCount) =>
        pipe(
            rankedNotes,
            nonEmptyArray.groupBy((rankedNote) => rankedNote.rankedHash),
            record.map(([firstNote, ...otherNotes]) =>
                otherNotes.reduce<CompactedNote>(
                    (acc, value) => {
                        acc.ids.push(value.id);
                        acc.serviceOrderIds.push(value.serviceOrderId);
                        return acc;
                    },
                    {
                        ...firstNote,
                        ids: [firstNote.id],
                        serviceOrderIds: [firstNote.serviceOrderId],
                    }
                )
            ),
            record.filter((note) => note.ids.length === checkedReservationCount),
            Object.values<CompactedNote>,
            orderServiceOrderNotes
        )
);
